import { Box, CircularProgress, MenuItem, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import type { PatientSummaryDto } from '~/backend/connector-next-api/connector-next-schemas';
import MenuFlyout from '~/components/Menu/MenuFlyout';
import {
    useAssignedToMe,
    useClinicalLists,
} from '~/containers/patient-list/hooks';
import { PatientIdentity } from '~/containers/patient-list/shared';
import { useToday } from '~/hooks/useToday';

/**
 * The patients assigned to the signed-in user, latest activity first, as a
 * flyout beside the menu row `anchorEl`: each named as the patient list names
 * it, and handed with its connector id to `onChoose` when chosen. A patient
 * with no connector id is shown but cannot be chosen. The patients are
 * fetched only while the flyout is open; the signed-in user they are filtered
 * by and the clinical tags and sites are read as soon as it is mounted. The
 * list says so when it cannot be loaded or names nobody but
 * `excludedPatientId`.
 */
export default function AssignedPatientsFlyout({
    anchorEl,
    open,
    onClose,
    excludedPatientId,
    onChoose,
}: {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    /** Connector id of a patient the list leaves out. */
    excludedPatientId: string | undefined;
    onChoose: (patient: PatientSummaryDto, patientId: string) => void;
}) {
    const today = useToday();
    const assigned = useAssignedToMe(open);
    const { data: lists } = useClinicalLists();
    const patients = assigned.patients.filter(
        (patient) => patient.id !== excludedPatientId
    );

    return (
        <MenuFlyout
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            width={280}
            data-testid="assigned-patients-menu"
        >
            {assigned.isLoading ? (
                <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={16} />
                </Box>
            ) : assigned.isError ? (
                <FlyoutNote error messageId="patientList.error.load" />
            ) : patients.length === 0 ? (
                <FlyoutNote messageId="switchPatient.noOther" />
            ) : (
                patients.map((patient, index) => {
                    const patientId = patient.id;
                    return (
                        <MenuItem
                            key={patientId ?? `unopenable-${index}`}
                            disabled={patientId === undefined}
                            onClick={() => {
                                if (patientId !== undefined) {
                                    onChoose(patient, patientId);
                                }
                            }}
                        >
                            <PatientIdentity
                                patient={patient}
                                today={today}
                                tags={lists.tags}
                            />
                        </MenuItem>
                    );
                })
            )}
        </MenuFlyout>
    );
}

/** A line in place of the list, muted, or in the error colour for a failure. */
function FlyoutNote({
    messageId,
    error = false,
}: {
    messageId: string;
    error?: boolean;
}) {
    return (
        <Typography
            variant="body2"
            color={error ? 'error.main' : 'text.secondary'}
            sx={{ px: 1.5, py: 0.75 }}
        >
            <FormattedMessage id={messageId} />
        </Typography>
    );
}
